import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useProjects } from '../hooks';
import { ProjectCard, ProjectSearch, CreateProjectModal, EditProjectModal } from '../ui';
import { ConfirmationModal } from '../../common';
import { Project } from '../../../types/project';

/**
 * ProjectsPage - Página de listagem de projetos
 *
 * Features:
 * - Grid de projetos
 * - Busca por nome/descrição
 * - Criar novo projeto
 * - Editar/Excluir projeto
 *
 * Rota: /projetos
 */
export const ProjectsPage = () => {
  const {
    projects,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    refresh,
    deleteProject,
    formatRelativeTime,
  } = useProjects();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleDeleteClick = (project: Project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject) return;

    try {
      setIsDeleting(true);
      await deleteProject(selectedProject.id);
      setShowDeleteModal(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleProjectCreated = () => {
    setShowCreateModal(false);
    refresh();
  };

  const handleProjectEdited = () => {
    setShowEditModal(false);
    setSelectedProject(null);
    refresh();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
          <span className="text-[#666666] text-sm">Carregando projetos...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-[#F4CC04] text-[#1a1a1a] rounded-lg font-medium hover:bg-[#f5d633] transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6 border-b border-[#333333]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <p className="text-[#666666] text-sm mb-1">Projetos</p>
            <h1 className="text-white text-xl md:text-2xl font-bold">Projetos</h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search */}
            <div className="flex-1 sm:flex-none sm:w-64">
              <ProjectSearch
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>

            {/* New Project Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="
                flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5
                bg-[#F4CC04] text-[#1a1a1a] rounded-lg
                font-medium hover:bg-[#f5d633]
                transition-colors flex-shrink-0
              "
              title="Novo Projeto"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Novo Projeto</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6">
        {projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-[#666666] text-lg mb-2">Nenhum projeto encontrado</p>
            <p className="text-[#444444] text-sm mb-6">
              {searchQuery
                ? 'Tente buscar por outro termo'
                : 'Crie seu primeiro projeto para organizar suas conversas'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="
                  flex items-center gap-2 px-4 py-2.5
                  bg-[#F4CC04] text-[#1a1a1a] rounded-lg
                  font-medium hover:bg-[#f5d633]
                  transition-colors
                "
              >
                <Plus className="w-5 h-5" />
                <span>Criar Projeto</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onEdit={handleEdit}
                onDelete={async (id) => {
                  const proj = projects.find(p => p.id === id);
                  if (proj) {
                    handleDeleteClick(proj);
                  }
                }}
                formatRelativeTime={formatRelativeTime}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleProjectCreated}
      />

      <EditProjectModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProject(null);
        }}
        onSuccess={handleProjectEdited}
        project={selectedProject}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Projeto"
        message={
          selectedProject
            ? `Tem certeza que deseja excluir o projeto "${selectedProject.name}"? ${
                selectedProject.chats_count && selectedProject.chats_count > 0
                  ? `Todas as ${selectedProject.chats_count} conversa${selectedProject.chats_count > 1 ? 's' : ''} ${selectedProject.chats_count > 1 ? 'serão perdidas' : 'será perdida'}.`
                  : 'Esta ação não pode ser desfeita.'
              }`
            : 'Tem certeza que deseja excluir este projeto?'
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedProject(null);
        }}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
};
